import { useNavigate } from "@tanstack/react-router"
import React, { useState } from "react"
import toast, { Toaster } from "react-hot-toast"

import { trpc } from "../lib/trpc"

interface Newsletter {
  id: string
  title: string
  subject?: string
  description?: string
  status: "draft" | "pending_approval" | "approved" | "sent" | "failed"
  createdAt: string
  updatedAt: string
  sentAt?: string
  createdBy: string
  approvedBy?: string
  approvedAt?: string
}

const statusColors = {
  draft: "bg-foregroundx text-gray-800",
  pending_approval: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  sent: "bg-blue-100 text-blue-800",
  failed: "bg-red-100 text-destructive",
}

const statusLabels = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  approved: "Approved",
  sent: "Sent",
  failed: "Failed",
}

export default function NewsletterScreen() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("")

  const {
    data: newsletters,
    isLoading,
    error,
    refetch,
  } = trpc.newsletter.list.useQuery({
    search: searchTerm || undefined,
    status: (statusFilter as any) || undefined,
    limit: 20,
  })

  const createNewsletter = trpc.newsletter.create.useMutation({
    onSuccess: (newsletter) => {
      toast.success("Newsletter created successfully!")
      navigate({ to: `/newsletter/${newsletter.id}` })
    },
    onError: (error) => {
      const errorMessage = error.message || "Unknown error occurred"
      toast.error(`Failed to create newsletter: ${errorMessage}`)
      console.error("Newsletter creation error:", error)
    },
  })

  const deleteNewsletter = trpc.newsletter.delete.useMutation({
    onSuccess: () => {
      toast.success("Newsletter deleted successfully!")
      void refetch()
    },
    onError: (error) => {
      const errorMessage = error.message || "Unknown error occurred"
      toast.error(`Failed to delete newsletter: ${errorMessage}`)
      console.error("Newsletter deletion error:", error)
    },
  })

  const handleCreateNewsletter = () => {
    const title = prompt("Enter newsletter title:")
    if (!title) return

    createNewsletter.mutate({
      title,
      selectedSources: [], // Will be selected in the editor
      description: "",
    })
  }

  const handleDeleteNewsletter = (id: string, title: string) => {
    if (confirm(`Are you sure you want to delete "${title}"?`)) {
      deleteNewsletter.mutate(id)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-foregroundx flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 ">Loading newsletters...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-foregroundx flex items-center justify-center">
        <div className="text-center text-destructive">
          <p className="text-xl font-semibold">Error loading newsletters</p>
          <p className="mt-2">{error.message}</p>
          <button onClick={() => refetch()} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-foregroundx">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold ">Newsletters</h1>
            <p className="mt-2 text-sm text-primary">
              Manage your internal newsletter campaigns with AI-powered content aggregation
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={handleCreateNewsletter}
              disabled={createNewsletter.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {createNewsletter.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Newsletter
                </>
              )}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-8 bg-background p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-primary">
                Search
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search newsletters..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-primary">
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Newsletter List */}
        <div className="mt-8">
          {newsletters && newsletters.length > 0 ? (
            <div className="bg-background shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {newsletters.map((newsletter: Newsletter) => (
                  <li key={newsletter.id}>
                    <div className="px-4 py-4 sm:px-6 ">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium  truncate">{newsletter.title}</h3>
                            <div className="ml-2 flex-shrink-0">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  statusColors[newsletter.status]
                                }`}
                              >
                                {statusLabels[newsletter.status]}
                              </span>
                            </div>
                          </div>

                          {newsletter.subject && <p className="mt-1 text-sm ">Subject: {newsletter.subject}</p>}

                          {newsletter.description && (
                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">{newsletter.description}</p>
                          )}

                          <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                            <span>Created: {formatDate(newsletter.createdAt)}</span>
                            <span>Updated: {formatDate(newsletter.updatedAt)}</span>
                            {newsletter.sentAt && <span>Sent: {formatDate(newsletter.sentAt)}</span>}
                          </div>
                        </div>

                        <div className="ml-4 flex items-center space-x-2">
                          <button
                            onClick={() => navigate({ to: `/newsletter/${newsletter.id}` })}
                            className=" hover:text-blue-900 text-sm font-medium"
                          >
                            Edit
                          </button>

                          {newsletter.status === "sent" && (
                            <button
                              onClick={() => navigate({ to: `/newsletter/${newsletter.id}/preview` })}
                              className="text-green-600 hover:text-green-900 text-sm font-medium"
                            >
                              View
                            </button>
                          )}

                          {newsletter.status === "draft" && (
                            <button
                              onClick={() => handleDeleteNewsletter(newsletter.id, newsletter.title)}
                              disabled={deleteNewsletter.isLoading}
                              className="text-destructive hover:text-red-900 text-sm font-medium disabled:opacity-50"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium ">No newsletters</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first newsletter.</p>
              <div className="mt-6">
                <button
                  onClick={handleCreateNewsletter}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Newsletter
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#333",
            color: "#fff",
          },
          success: {
            style: {
              background: "#10B981",
            },
          },
          error: {
            style: {
              background: "#EF4444",
            },
          },
        }}
      />
    </div>
  )
}
